/**
 * Auto Raise Delivery Appeal section.
 *
 * CR-7068 added the switch. CR-8283 made it each customer's own: an appeal is
 * raised automatically only when the delivery is late or early against the
 * agreed date but on time against a new date the supplier had asked for, and
 * the customer had not answered that request when the goods were booked in.
 * Only deliveries booked in from the day it was turned on are included, so the
 * section shows that day. Off by default, and never inherited from the
 * platform value — so a Helix user is told each customer sets it instead of
 * being offered a switch that would change nothing.
 */

import { useState, useEffect } from 'react';
import { platformSettingsAPI } from '../../../lib/api';
import { isHelixPlatformUser } from '../../../lib/adminSetupAccess';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, Bell, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export function AutoRaiseDeliveryAppealSection() {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const isHelix = isHelixPlatformUser(user);
  const [loading, setLoading] = useState(!isHelix);
  // A failed load must not show the default (OFF): a customer with it ON would
  // be told it is off. The section says it could not load instead.
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [enabledOn, setEnabledOn] = useState<string | null>(null);

  useEffect(() => {
    if (isHelix) return;
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        setEnabled(!!data.auto_raise_delivery_appeals);
        setEnabledOn(data.auto_raise_delivery_appeals_enabled_on ?? null);
      } catch (err: unknown) {
        console.error('Error loading auto-raise setting:', err);
        toast.error('Failed to load Auto Raise Delivery Appeal setting');
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [isHelix]);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    setSaving(true);
    try {
      const { data } = await platformSettingsAPI.patchSettings({ auto_raise_delivery_appeals: checked });
      setEnabled(!!data.data.auto_raise_delivery_appeals);
      setEnabledOn(data.data.auto_raise_delivery_appeals_enabled_on ?? null);
      toast.success(`Auto Raise Delivery Appeals: ${checked ? 'ON' : 'OFF'}`);
    } catch (err: unknown) {
      console.error('Error saving auto-raise setting:', err);
      toast.error('Failed to save Auto Raise Delivery Appeal setting');
      setEnabled(previous);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Auto Raise Delivery Appeals
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          When enabled, a delivery appeal is raised for the supplier when goods arrive late or early
          against the agreed date but on time against a new date the supplier had asked for, and that
          request was still waiting for the customer&apos;s answer when the goods were booked in. Only deliveries
          booked in from the day it is turned on are included.
        </p>
      </CardHeader>
      <CardContent>
        {isHelix ? (
          <p className="rounded-md border border-input bg-background px-4 py-3 text-sm text-muted-foreground">
            Each customer&apos;s admin turns this on for their own company in Admin Setup.
          </p>
        ) : loadFailed ? (
          <p role="alert" className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            This setting could not be loaded. Reload the page to try again.
          </p>
        ) : (
          <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
            <div className="space-y-1 pr-3">
              <Label htmlFor="auto-raise-delivery-appeal" className="text-sm font-medium">
                Auto Raise Delivery Appeals
              </Label>
              <p className="text-xs text-muted-foreground">
                {enabled
                  ? `ON${enabledOn ? ` since ${formatDate(enabledOn)}` : ''} — Appeals are raised automatically for qualifying deliveries.`
                  : 'OFF — Suppliers must manually raise delivery appeals.'}
              </p>
            </div>
            <Switch
              id="auto-raise-delivery-appeal"
              checked={enabled}
              disabled={saving}
              onCheckedChange={handleToggle}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
